/// <amd-module name="Repos/CommitsList/Components/HistoryGraph" />

import { WebClientGraphRow, WebClientVisualizationCell } from "Repos/Common/Contracts/History";
import {
    ExcisionVisComponents,
    HighlightDirection,
    HistoryGraphOrientation,
    IDictionaryNumberTo,
    IDictionaryStringTo,
    IHistoryGraph,
    IHistoryGraphExcisionSettings,
    IHistoryGraphRow,
    IHistoryGraphSettings,
    VisComponents,
    VisualizationCell
} from "./HistoryGraphCell.Props";

import { ObservableValue } from "VSS/Core/Observable";

import { ExcisionVisComponentsHelper, VisComponentsHelper } from "./HistoryGraphHelper";
import { HistoryGraphRowGroup } from "./HistoryGraphRowGroup";

/**
 * Model for the git history graph
 * - Graph model here is primarily made up of only rows (IHistoryGraphRow) which hold both commit data and visual metadata
 * - Each row in the graph has 'n' number of cells(VisualizationCell), which on render will be horizontally stacked
 * - Each cell holds the data which dictates what should be drawn/rendered in it
 *
 * e.g. Assume we have 3 commits, 2 are in one branch and 1 is in another.
 * - The visual representation would be,
 *
 *               +++++++++++++++++++
 *               +     +     +     +
 *               +  O  +     +     +
 *               +  |\ +     +     +        Row 1 (cell 0, cell 1, cell 2)
 *               +  | \+     +     +
 *               +++|++\++++++++++++
 *               +  |  +\    +     +
 *               +  |  +  O  +     +
 *               +  |  + /   +     +        Row 2 (cell 0, cell 1, cell 2)
 *               +  |  +/    +     +
 *               +++|++/++++++++++++
 *               +  | /+     +     +
 *               +  O  +     +     +
 *               +  |  +     +     +        Row 3 (cell 0, cell 1, cell 2)
 *               +  |  +     +     +
 *               +++++++++++++++++++
 *
 */
export class HistoryGraph implements IHistoryGraph {
    public rows: IDictionaryStringTo<ObservableValue<IHistoryGraphRow>>;
    public rowIdCommitIdMap: IDictionaryNumberTo<string>;
    public settings: IHistoryGraphSettings;
    public selectedCommitId: ObservableValue<string | null>;
    public selectedExcisedParentCommitIds: string[];
    public selectedExcisedChildCommitIds: string[];
    public selectedOldestExcisedParentRowId: number | null;
    public selectedYoungestExcisedChildRowId: number | null;

    constructor(webClientGraphRows: WebClientGraphRow[]) {
        const graphRows: IDictionaryStringTo<ObservableValue<IHistoryGraphRow>> = {};
        const rowIdCommitIdMap: IDictionaryNumberTo<string> = {};

        webClientGraphRows.forEach((row: WebClientGraphRow, index: number) => {
            const aboveRow = webClientGraphRows[index - 1];
            const belowRow = webClientGraphRows[index + 1];

            const constructedRow: IHistoryGraphRow = new HistoryGraphRowGroup({
                id: index,
                commit: row.commit,
                cells: this.getGraphCellFromWebClient(row.cells),
                maxCellId: row.maxCellId,
                commitCellId: row.commitLane,
                hasIncomingExcisedCommits: row.hasIncomingExcisedCommits,
                hasOutgoingExcisedCommits: row.hasOutgoingExcisedCommits,
                aboveCommitId: aboveRow ? aboveRow.commit.commitId : "",
                belowCommitId: belowRow ? belowRow.commit.commitId : ""
            });
            graphRows[row.commit.commitId] = new ObservableValue<IHistoryGraphRow>(constructedRow);
            rowIdCommitIdMap[index] = row.commit.commitId;
        });

        this.populateChildAndParentCommitIds(graphRows);

        this.setParentGraph();

        this.rows = graphRows;
        this.rowIdCommitIdMap = rowIdCommitIdMap;
        this.settings = {
            orientation: HistoryGraphOrientation.LeftJustify,
            excisionSettings: this.getDefaultExcisionSettings()
        };

        this.selectedCommitId = new ObservableValue<string | null>(null);
        this.selectedExcisedParentCommitIds = [];
        this.selectedExcisedChildCommitIds = [];
        this.selectedOldestExcisedParentRowId = null;
        this.selectedYoungestExcisedChildRowId = null;
    }

    /**
     * Update selectedCommit in graph and perform highlight operation on the graph rows
     * This highlights the paths leading to the selectedCommit and highlights the paths leading away from the selectedCommit
     * @param selectedCommitId
     */
    public select(selectedCommitId: string): void {
        // Can skip if this is same as the previously selected commit
        if (this.selectedCommitId.value === selectedCommitId) {
            return;
        }

        // Unselect the previously selected commit
        if (this.selectedCommitId) {
            this.unSelect();
        }

        // Return if the commit id is not present. Do the unselect before returning.
        if (!selectedCommitId || !this.rows[selectedCommitId]) {
            return;
        }

        // Update the current selected commit id
        this.selectedCommitId.value = selectedCommitId;

        const selectedRow: IHistoryGraphRow = this.rows[this.selectedCommitId.value].value;
        if (selectedRow.hasIncomingExcisedCommits) {
            selectedRow.childCommitIds.forEach((childCommitId: string) => {
                if (this.rows[childCommitId]) {
                    if (this.rows[childCommitId].value.hasOutgoingExcisedCommits) {
                        // Add the child to the selected childs list
                        this.selectedExcisedChildCommitIds.push(childCommitId);

                        // Update the youngest selected child row id
                        if (!this.selectedYoungestExcisedChildRowId) {
                            this.selectedYoungestExcisedChildRowId = this.rows[childCommitId].value.id;
                        } else {
                            this.selectedYoungestExcisedChildRowId = Math.min(
                                this.rows[childCommitId].value.id,
                                this.selectedYoungestExcisedChildRowId
                            );
                        }

                        // Highlight rows starting from the child row
                        this.highlightRows(this.rows[childCommitId].value, HighlightDirection.FromBelow);
                    }
                } else {
                    // This scenario is not possible, since child is always loaded if the parent is loaded, but for completeness
                    this.selectedYoungestExcisedChildRowId = Number.MIN_VALUE;
                }
            });
        } else {
            this.selectedYoungestExcisedChildRowId = selectedRow.id;
        }

        if (selectedRow.hasOutgoingExcisedCommits) {
            selectedRow.parentCommitIds.forEach((parentCommitId: string) => {
                if (this.rows[parentCommitId]) {
                    const parentRow = this.rows[parentCommitId].value;
                    if (parentRow.hasIncomingExcisedCommits) {
                        // Add the parent to the selected parents list
                        this.selectedExcisedParentCommitIds.push(parentCommitId);

                        // Update the oldest selected parent row id
                        if (!this.selectedOldestExcisedParentRowId) {
                            this.selectedOldestExcisedParentRowId = parentRow.id;
                        } else {
                            this.selectedOldestExcisedParentRowId = Math.max(parentRow.id, this.selectedOldestExcisedParentRowId);
                        }

                        // Highlight rows starting from the parent row
                        this.highlightRows(parentRow, HighlightDirection.FromAbove);

                        this.rows[parentCommitId].value = parentRow;
                    }
                } else {
                    this.selectedOldestExcisedParentRowId = Number.MAX_VALUE;
                }
            });
        } else {
            this.selectedOldestExcisedParentRowId = selectedRow.id;
        }

        // Highlighting rows starting from the current selected row
        this.highlightRows(selectedRow);
        this.setParentGraph();

        this.rows[this.selectedCommitId.value].value = selectedRow;
    }

    /**
     * Perform unHighlight operation on the row corresponding to the selected commit
     */
    public unSelect(): void {
        if (this.selectedCommitId) {
            this.unHighlightRows();

            this.selectedCommitId.value = null;
            this.selectedExcisedChildCommitIds = [];
            this.selectedExcisedParentCommitIds = [];
            this.selectedOldestExcisedParentRowId = null;
            this.selectedYoungestExcisedChildRowId = null;
        }
    }

    public getDefaultExcisionSettings(): IHistoryGraphExcisionSettings {
        return {
            excisionEnabled: true,
            emptyLineLengthLimit: 20,
            emptyLineLengthMultiplier: 1
        };
    }

    /**
     * Highlights the rows of the graph starting from the row provided.
     * Step 1: highlights the provided row horizontally
     * Step 2: highlights the rows above the startingRow
     * Step 3: highlights the rows below the stratingRow
     * @param startingRow
     * @param highlightDirection
     */
    private highlightRows(startingRow: IHistoryGraphRow, highlightDirection?: HighlightDirection): void {
        if (!highlightDirection) {
            highlightDirection = HighlightDirection.All;
        }

        if (!startingRow.cells || Object.keys(startingRow.cells).length === 0) {
            return;
        }

        const highlightAbove = highlightDirection === HighlightDirection.All || highlightDirection === HighlightDirection.FromBelow;
        const highlightBelow = highlightDirection === HighlightDirection.All || highlightDirection === HighlightDirection.FromAbove;

        // Highlight the starting row horizontally before highlighting above and below
        startingRow.highlightHorizontal(startingRow.commitCellId, HighlightDirection.SelectionRow, highlightAbove, highlightBelow);

        const rowCount = Object.keys(this.rows).length;
        for (let i = 1; i < rowCount; i++) {
            let withinBounds = false;
            if (i <= startingRow.id && highlightAbove) {
                const row: IHistoryGraphRow = this.rows[this.rowIdCommitIdMap[startingRow.id - i]].value;
                if (row && row.belowCommitId) {
                    withinBounds = true;
                    row.highlightFromBelow(this.rows[row.belowCommitId].value.cells);
                }
                this.rows[this.rowIdCommitIdMap[startingRow.id - i]].value = row;
            }
            if (startingRow.id + i < rowCount && highlightBelow) {
                const row: IHistoryGraphRow = this.rows[this.rowIdCommitIdMap[startingRow.id + i]].value;
                if (row && row.aboveCommitId) {
                    withinBounds = true;
                    row.highlightFromAbove(this.rows[row.aboveCommitId].value.cells);
                }
                this.rows[this.rowIdCommitIdMap[startingRow.id + i]].value = row;
            }
            if (!withinBounds) {
                break;
            }
        }
    }

    /**
     * Un-highlights the rows in the graph
     */
    private unHighlightRows(): void {
        for (const commitId in this.rows) {
            const row: IHistoryGraphRow = this.rows[commitId].value;
            row.unHighlight();
            this.rows[commitId].value = row;
        }
    }

    private populateChildAndParentCommitIds(graphRows: IDictionaryStringTo<ObservableValue<IHistoryGraphRow>>): void {
        for (const commitId in graphRows) {
            const graphRow = graphRows[commitId].value;
            if (graphRow.commit.parents && graphRow.commit.parents.length > 0) {
                graphRow.commit.parents
                    .map(parentCommitId => {
                        if (graphRows[parentCommitId]) {
                            const parentGraphRow = graphRows[parentCommitId].value;
                            if (parentGraphRow.childCommitIds == undefined) {
                                parentGraphRow.childCommitIds = [];
                            }

                            parentGraphRow.childCommitIds.push(graphRow.commit.commitId);
                            return parentCommitId;
                        } else {
                            // parent is present but not yet loaded into the history list
                            return "-1";
                        }
                    })
                    .forEach((parentCommitId: string) => {
                        if (graphRow.parentCommitIds == undefined) {
                            graphRow.parentCommitIds = [];
                        }

                        // adding parent row ids to current row
                        graphRow.parentCommitIds.push(parentCommitId);
                    });
            }
            graphRows[commitId].value = graphRow;
        }

        for (const index in graphRows) {
            if (graphRows[index].value.childCommitIds) {
                const graphCopy = graphRows[index].value;
                graphCopy.childCommitIds.sort();
                graphRows[index].value = graphCopy;
            }

            if (graphRows[index].value.parentCommitIds) {
                const graphCopy = graphRows[index].value;
                graphCopy.parentCommitIds.sort();
                graphRows[index].value = graphCopy;
            }
        }
    }

    private getGraphCellFromWebClient(cells: IDictionaryNumberTo<WebClientVisualizationCell> | null): VisualizationCell[] {
        const constructedCells: VisualizationCell[] = [];

        if (cells) {
            for (const i in cells) {
                const index = Number(i);
                const cell = cells[index];
                const components =
                    typeof cell.components === "string" ? VisComponentsHelper.GetComponentsFromString(cell.components) : cell.components;
                const excisionComponents =
                    typeof cell.excisionComponents === "string"
                        ? ExcisionVisComponentsHelper.GetComponentsFromString(cell.excisionComponents)
                        : cell.excisionComponents;

                constructedCells[index] = {
                    id: index,
                    components,
                    excisionComponents,
                    highlitComponents: VisComponents.None,
                    highlitExcisionComponents: ExcisionVisComponents.None
                } as VisualizationCell;
            }
        }

        return constructedCells;
    }

    private setParentGraph(): void {
        for (const commitId in this.rows) {
            const graphRow = this.rows[commitId].value;
            graphRow.parentGraph = this;
            this.rows[commitId].value = graphRow;
        }
    }
}
